import { useState, useEffect, useCallback, useRef } from 'react';
import { Bot, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { ticketsAPI, conversationsAPI } from '@/lib/api';
import { fechaYHora } from '@/lib/fechas';

/**
 * Si hay un agente trabajando en este caso ahora, y qué pasó con el que no terminó.
 *
 * **El caso que lo motivó** (2026-09-30). El ticket 20260930-1 quedó sin su README. El turno de
 * resolución arrancó con el README como acción requerida, llegó a la ronda 2, y un deploy
 * reemplazó el contenedor del backend. El turno no cerró: ni satisfecho, ni agotado, ni fallido.
 * En la autopsia el último nodo era una herramienta y después no había nada — desde afuera era
 * indistinguible de un turno colgado, y de un turno que nunca existió.
 *
 * **"Trabajando" no puede mentir.** El estado lo deriva el backend de hechos (qué proceso lo
 * corre, cuándo vence) y nunca de un campo que alguien tenga que actualizar: lo que se murió es
 * justo lo único que podría escribir que se murió. Acá sólo se pinta lo que llega.
 *
 * **Y un agente trabajando ahora no tapa un turno anterior trunco.** Son dos preguntas
 * independientes y esconder la segunda porque existe la primera es como empezó este problema.
 */
const ORIGENES = {
  email: 'por correo',
  whatsapp: 'por WhatsApp',
  webchat: 'por el chat web',
  web: 'por el chat web',
  teams: 'por Teams',
  asistente_ia: 'desde el Asistente IA de esta ficha',
  coordinador: 'por el coordinador del caso',
  tarea: 'por una tarea asignada',
  consulta_libre: 'desde una consulta libre',
  ensayo: 'desde un ensayo de agente',
  desconocido: '',
};

const nombreDelOrigen = (o) => ORIGENES[o] ?? `por ${o}`;

function haceCuanto(iso) {
  if (!iso) return '';
  const seg = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seg < 60) return `hace ${seg} s`;
  const min = Math.round(seg / 60);
  if (min < 60) return `hace ${min} min`;
  const hs = Math.round(min / 60);
  if (hs < 24) return `hace ${hs} h`;
  return fechaYHora(iso);
}

export default function ActividadAgentica({ tenantId, capsuleId, ticketId, conversationId }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  //: Lo que se observa **ya no esta**. Es distinto de "no hay nada que mostrar": uno se dice, el
  //: otro no se dibuja.
  const [noExiste, setNoExiste] = useState(false);
  const vivo = useRef(true);

  /**
   * Trae el estado, y distingue **«todavia no» de «ya no»**.
   *
   * El `catch` de antes devolvia `null` para cualquier error y el ciclo se reprogramaba igual. Con
   * un 404 eso es un bucle infinito: el 2026-10-02, despues de un borrado operativo, una pestaña
   * abierta en un caso borrado pidio su actividad **cada 30 segundos durante una hora**, y cada
   * intento escribio dos lineas de `diagnostic_logs` —una del frontend y una del backend— en el log
   * que se acababa de limpiar justamente para poder leerlo.
   *
   * Un 404 no es un error transitorio: el caso **no esta**, y volver a preguntar no lo trae. Se
   * corta el ciclo. Un 500 o un corte de red si son transitorios y se siguen intentando, porque ahi
   * reintentar es exactamente lo correcto.
   */
  const traer = useCallback(async () => {
    if (!tenantId || !capsuleId || (!ticketId && !conversationId)) return { fin: true };
    try {
      const r = ticketId
        ? await ticketsAPI.actividadAgentica(tenantId, capsuleId, ticketId)
        : await conversationsAPI.actividadAgentica(tenantId, capsuleId, conversationId);
      return { datos: r.data };
    } catch (e) {
      if (e?.response?.status === 404) return { noExiste: true };
      // Observar no puede romper la pantalla: sin datos no se muestra nada, y se reintenta.
      return { datos: null };
    }
  }, [tenantId, capsuleId, ticketId, conversationId]);

  useEffect(() => {
    vivo.current = true;
    setNoExiste(false);
    let timer;
    const ciclo = async () => {
      const r = await traer();
      if (!vivo.current) return;
      if (r.fin) { setCargando(false); return; }
      if (r.noExiste) {
        // **No se reprograma.** Preguntar otra vez por algo que no existe no lo hace aparecer, y
        // deja el log lleno de 404 sobre un caso borrado.
        setNoExiste(true);
        setCargando(false);
        return;
      }
      setDatos(r.datos);
      setCargando(false);
      // Mientras hay alguien trabajando se mira seguido, porque la persona está esperando.
      // Cuando no, el caso cambia por minutos y no por segundos.
      timer = setTimeout(ciclo, r.datos?.hay_agente_trabajando ? 5000 : 30000);
    };
    ciclo();
    return () => { vivo.current = false; clearTimeout(timer); };
  }, [traer]);

  // **Se dice, y no se calla.** La pestaña quedo abierta en algo que ya no esta: sin este cartel la
  // pantalla se ve igual que cuando no hay nada que mostrar, y quien la mira cree que el caso sigue
  // ahi y que no pasa nada.
  if (noExiste) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="actividad-sin-caso">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
        <span>
          {ticketId ? 'Este caso ya no existe' : 'Esta conversación ya no existe'}: lo borraron
          mientras tenías la pantalla abierta. Recargá para ver lo que hay ahora.
        </span>
      </div>
    );
  }

  if (cargando || !datos) return null;

  const { en_curso: enCurso = [], ultimo_interrumpido: trunco, turnos = [] } = datos;
  const ultimo = turnos[0];
  const degradado =
    !enCurso.length && !trunco && ultimo?.estado === 'cerrado'
    && (ultimo?.salud?.status && ultimo.salud.status !== 'ok');

  if (!enCurso.length && !trunco && !degradado) {
    if (!ultimo) return null;
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
        <span>
          Sin agentes trabajando. El último turno cerró bien, {haceCuanto(ultimo.abierto_at)}.
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {enCurso.map((t) => (
        <div
          key={t.turn_id}
          className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2
                     dark:border-emerald-900 dark:bg-emerald-950/40"
        >
          <span className="relative mt-1 flex h-2 w-2 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-600" />
          </span>
          <div className="min-w-0 text-xs">
            <div className="font-medium text-emerald-900 dark:text-emerald-200">
              <Bot className="mr-1 inline h-3.5 w-3.5" />
              {t.agent_id} está trabajando ahora
            </div>
            <div className="text-emerald-800/80 dark:text-emerald-300/80">
              Entró {nombreDelOrigen(t.origen)} · arrancó {haceCuanto(t.abierto_at)}
            </div>
          </div>
          <Loader2 className="ml-auto h-3.5 w-3.5 shrink-0 animate-spin text-emerald-600" />
        </div>
      ))}

      {trunco && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2
                     dark:border-amber-900 dark:bg-amber-950/40"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="min-w-0 text-xs">
            <div className="font-medium text-amber-900 dark:text-amber-200">
              Un turno se frenó sin terminar
            </div>
            <div className="text-amber-800/90 dark:text-amber-300/90">
              {trunco.agent_id}, {haceCuanto(trunco.abierto_at)} · {trunco.motivo}
            </div>
            {!!(trunco.que_quedo_sin_hacer || []).length && (
              <div className="mt-1 text-amber-900 dark:text-amber-200">
                Quedó sin{' '}
                {trunco.que_quedo_sin_hacer.join('; sin ')}.
              </div>
            )}
            <div className="mt-1 text-amber-800/70 dark:text-amber-300/70">
              Un mensaje nuevo en el caso vuelve a exigírselo al agente.
            </div>
          </div>
        </div>
      )}

      {degradado && (
        <div className="flex items-start gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2
                        text-xs dark:border-zinc-800 dark:bg-zinc-900/60">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="min-w-0 space-y-1">
            <div className="font-medium">
              El último turno cerró con problemas · {haceCuanto(ultimo.abierto_at)}
            </div>
            {/*
              La frase, no el código. La pantalla mostraba `tool_failednarracion_interna` —dos
              identificadores internos pegados— y eso no le dice nada a quien opera. La tabla
              que explica cada código vive en `turn_health` y viaja traducida: repetirla acá
              sería la segunda declaración que después diverge.
            */}
            <ul className="space-y-0.5 text-muted-foreground">
              {(ultimo.salud_legible || []).map((h) => (
                <li key={h.codigo} className="flex gap-1.5">
                  <span aria-hidden className="select-none">·</span>
                  <span>{h.mensaje}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
